import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ChevronRight, Filter, Grid, List } from "lucide-react";
import { useApp } from "../context/AppContext";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const CategoryPage = () => {
  const { slug } = useParams();
  const { API } = useApp();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("default");
  const [viewMode, setViewMode] = useState("grid");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/products/category/${slug}`);
        setCategory(response.data.category);
        setProducts(response.data.products);
      } catch (error) {
        console.error("Failed to fetch category:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, API]);

  const sortedProducts = [...products].sort((a, b) => {
    switch (sortBy) {
      case "price-low":
        return a.price - b.price;
      case "price-high":
        return b.price - a.price;
      case "name":
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="animate-pulse space-y-8">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="product-grid">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="space-y-3">
                <div className="aspect-[4/5] bg-muted rounded-xl" />
                <div className="h-4 bg-muted rounded w-3/4" />
                <div className="h-6 bg-muted rounded w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h2 className="text-2xl font-bold mb-4">Category Not Found</h2>
        <Link to="/categories">
          <Button data-testid="back-to-categories">Browse All Categories</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="category-page">
      {/* Hero Banner */}
      <div className="relative h-48 md:h-64 bg-muted overflow-hidden">
        <img
          src={category.image_url || "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=1200"}
          alt={category.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30" />
        <div className="relative z-10 container mx-auto px-4 md:px-6 lg:px-8 h-full flex flex-col justify-center">
          {/* Breadcrumb */}
          <nav className="breadcrumb text-white/80 mb-4" data-testid="breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/categories" className="hover:text-white">Categories</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{category.name}</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold text-white" data-testid="category-title">
            {category.name}
          </h1>
          {category.description && (
            <p className="text-white/80 mt-2 max-w-xl">{category.description}</p>
          )}
        </div>
      </div>

      {/* Products Section */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
        {/* Filters Bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 pb-6 border-b">
          <p className="text-muted-foreground" data-testid="product-count">
            <span className="font-semibold text-foreground">{products.length}</span> products found
          </p>
          
          <div className="flex items-center gap-4">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-48" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="name">Name: A to Z</SelectItem>
              </SelectContent>
            </Select>

            <div className="hidden sm:flex items-center gap-2">
              <Button
                variant={viewMode === "grid" ? "default" : "ghost"}
                size="icon"
                onClick={() => setViewMode("grid")}
                data-testid="view-grid"
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="icon"
                onClick={() => setViewMode("list")}
                data-testid="view-list"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {sortedProducts.length > 0 ? (
          <div className={viewMode === "grid" ? "product-grid" : "space-y-4"}>
            {sortedProducts.map((product, index) => (
              <div key={product.id} className={`animate-fade-in-up stagger-${(index % 4) + 1}`}>
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state py-20" data-testid="empty-state">
            <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-6">
              <Filter className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-2">No Products Found</h3>
            <p className="text-muted-foreground mb-6">
              We couldn't find any products in this category.
            </p>
            <Link to="/categories">
              <Button data-testid="browse-other-categories">Browse Other Categories</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
